import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import {
  collection,
  addDoc,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import '../../css/accounts/CouponCode.css';

const CouponCode = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage', // 'percentage' or 'amount'
    discountValue: '',
    minOrderAmount: '',
    usageLimit: '',
    expiryDate: '',
    isActive: true,
  });

  // Fetch coupons real-time from Firestore 'couponCode' collection
  useEffect(() => {
    const q = query(collection(db, 'couponCode'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setCoupons(list);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching coupon codes:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle Create Coupon
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      alert('Please enter a valid coupon code.');
      return;
    }

    if (!formData.discountValue || Number(formData.discountValue) <= 0) {
      alert('Please enter a valid discount value greater than 0.');
      return;
    }

    if (formData.discountType === 'percentage' && Number(formData.discountValue) > 100) {
      alert('Percentage discount cannot exceed 100%.');
      return;
    }

    setIsSubmitting(true);

    try {
      const formattedCode = formData.code.trim().toUpperCase();

      await addDoc(collection(db, 'couponCode'), {
        code: formattedCode,
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue),
        minOrderAmount: formData.minOrderAmount ? Number(formData.minOrderAmount) : 0,
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
        usedCount: 0,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : null,
        isActive: formData.isActive,
        createdAt: serverTimestamp(),
      });

      alert(`Coupon "${formattedCode}" created successfully!`);

      // Reset Form
      setFormData({
        code: '',
        discountType: 'percentage',
        discountValue: '',
        minOrderAmount: '',
        usageLimit: '',
        expiryDate: '',
        isActive: true,
      });
    } catch (error) {
      console.error('Error adding coupon code:', error);
      alert('Failed to create coupon code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle Active/Inactive Status
  const handleToggleStatus = async (coupon) => {
    try {
      const couponRef = doc(db, 'couponCode', coupon.id);
      await updateDoc(couponRef, {
        isActive: !coupon.isActive,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status.');
    }
  };

  // Delete Coupon
  const handleDelete = async (id, code) => {
    if (window.confirm(`Are you sure you want to delete the coupon "${code}"?`)) {
      try {
        await deleteDoc(doc(db, 'couponCode', id));
      } catch (error) {
        console.error('Error deleting coupon:', error);
        alert('Failed to delete coupon.');
      }
    }
  };

  // Filter Search
  const filteredCoupons = coupons.filter((c) =>
    (c.code || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="coupon-container">
      <div className="coupon-grid">
        {/* Left Side: Create Coupon Form */}
        <div className="coupon-card coupon-form-card">
          <div className="card-header">
            <h3>🏷️ Create New Coupon Code</h3>
          </div>
          <form onSubmit={handleSubmit} className="coupon-form">
            <div className="form-group">
              <label>Coupon Code *</label>
              <input
                type="text"
                name="code"
                placeholder="e.g. SAVE20 or MRWHITE50"
                value={formData.code}
                onChange={handleChange}
                required
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Discount Type *</label>
                <select
                  name="discountType"
                  value={formData.discountType}
                  onChange={handleChange}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="amount">Fixed Amount (AED)</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  Discount Value * ({formData.discountType === 'percentage' ? '%' : 'AED'})
                </label>
                <input
                  type="number"
                  name="discountValue"
                  placeholder={formData.discountType === 'percentage' ? 'e.g. 15' : 'e.g. 50'}
                  value={formData.discountValue}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Min Order Amount (AED)</label>
                <input
                  type="number"
                  name="minOrderAmount"
                  placeholder="e.g. 100 (Optional)"
                  value={formData.minOrderAmount}
                  onChange={handleChange}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Max Usage Limit</label>
                <input
                  type="number"
                  name="usageLimit"
                  placeholder="e.g. 500 (Optional)"
                  value={formData.usageLimit}
                  onChange={handleChange}
                  min="1"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Expiry Date</label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="switch-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span>Active Immediately</span>
              </label>
            </div>

            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving Coupon...' : '➕ Add Coupon Code'}
            </button>
          </form>
        </div>

        {/* Right Side: Coupons List */}
        <div className="coupon-card coupon-list-card">
          <div className="card-header list-header">
            <h3>📋 Existing Coupons</h3>
            <input
              type="text"
              className="coupon-search"
              placeholder="🔍 Search Coupon Code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="table-responsive">
            <table className="coupon-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Min Order</th>
                  <th>Usage Limit</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="table-loader">
                      Loading coupon codes...
                    </td>
                  </tr>
                ) : filteredCoupons.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="table-loader">
                      No coupon codes found.
                    </td>
                  </tr>
                ) : (
                  filteredCoupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td className="code-cell">
                        <strong>{coupon.code}</strong>
                      </td>
                      <td className="discount-cell">
                        {coupon.discountType === 'percentage'
                          ? `${coupon.discountValue}% OFF`
                          : `AED ${Number(coupon.discountValue).toFixed(2)} OFF`}
                      </td>
                      <td>
                        {coupon.minOrderAmount
                          ? `AED ${Number(coupon.minOrderAmount).toFixed(2)}`
                          : 'No Min'}
                      </td>
                      <td>
                        {coupon.usageLimit
                          ? `${coupon.usedCount || 0} / ${coupon.usageLimit}`
                          : 'Unlimited'}
                      </td>
                      <td>
                        {coupon.expiryDate
                          ? (coupon.expiryDate.toDate
                              ? coupon.expiryDate.toDate()
                              : new Date(coupon.expiryDate)
                            ).toLocaleDateString('en-GB')
                          : 'No Expiry'}
                      </td>
                      <td>
                        <button
                          className={`status-badge-btn ${coupon.isActive ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleStatus(coupon)}
                        >
                          {coupon.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(coupon.id, coupon.code)}
                          title="Delete Coupon"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponCode;